import React from "react";
import { Moon, Sun } from "lucide-react";
import useThemePreference from "../../utils/useThemePreference";

const VARIANT_CLASSES = {
  // Neutral pill that adapts to the host page (works inside the orange-light
  // header and inside the Bulandi dark theme alike).
  default:
    "inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 shadow-sm transition hover:border-orange-400 hover:text-orange-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 dark:border-white/15 dark:bg-white/5 dark:text-gray-100 dark:hover:border-bul-lime/60 dark:hover:text-bul-lime dark:focus-visible:ring-bul-lime dark:focus-visible:ring-offset-transparent",

  // Full-width row used inside the mobile menu.
  menuRow:
    "flex w-full items-center justify-between rounded-lg border border-gray-200 px-3 py-2 text-left text-gray-700 transition hover:border-orange-400 hover:text-orange-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 dark:border-white/15 dark:text-gray-100 dark:hover:border-bul-lime/60 dark:hover:text-bul-lime",

  // Variant used on the Bulandi page where backgrounds are already dark/light
  // tokens — uses bul-* tokens so it blends with the rest of that page.
  bulandi:
    "inline-flex h-9 w-9 items-center justify-center rounded-full border-2 border-bul-border bg-bul-surface/70 text-bul-fg shadow-bul-brutal-sm transition hover:border-bul-lime hover:text-bul-lime focus:outline-none focus-visible:ring-2 focus-visible:ring-bul-lime focus-visible:ring-offset-2 focus-visible:ring-offset-bul-bg",
};

const ThemeToggle = ({ variant = "default", className = "", iconSize = 18 }) => {
  const { isDark, toggleTheme } = useThemePreference();
  const nextLabel = isDark ? "Switch to light mode" : "Switch to dark mode";
  const Icon = isDark ? Sun : Moon;

  if (variant === "menuRow") {
    return (
      <button
        type="button"
        onClick={toggleTheme}
        aria-label={nextLabel}
        title={nextLabel}
        className={`${VARIANT_CLASSES.menuRow} ${className}`.trim()}
      >
        <span className="flex items-center gap-2 font-semibold capitalize">
          <Icon size={iconSize} />
          {isDark ? "Light mode" : "Dark mode"}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-300">Tap to switch</span>
      </button>
    );
  }

  const variantClass = VARIANT_CLASSES[variant] || VARIANT_CLASSES.default;

  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={nextLabel}
      title={nextLabel}
      aria-pressed={isDark}
      className={`${variantClass} ${className}`.trim()}
    >
      <Icon size={iconSize} aria-hidden="true" />
      <span className="sr-only">{nextLabel}</span>
    </button>
  );
};

export default ThemeToggle;
